import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { get } from "lodash";
import { useNavigate } from "react-router-dom";
import { PruToast } from "../../../../components/pru-toast";
import { Region } from "../../../../components/region-phone/region-phone";
import { ONBOARDING_PATH, ROOT_PATH } from "../../../../routes/constants";
import { getGlobalConfigAsync, loginAsync, reset } from "../../../auth/redux/auth-slice";
import { reset as onboardingReset } from "../../../onboarding/redux/onboarding-slice";
import {
  selectCandidateProfile,
  selectError,
  selectIsLoading,
  selectToken,
} from "../../../auth/redux/selector";
import { initSurvey } from "../../components/surveyjs/survey/init-survey";
import { fetchGlobalConfig, fetchRecruitmentPublicConfig } from "../../network/network";
import { RoleEnum } from "../../types";
import {
  backToApp,
  COMMON_PUBLIC_FRONTEND_ENDPOINT,
  CORE_URL,
  fakeToken,
  getLoginUserData,
  getTargetCandidate /* PHKL Custom */,
  getToken,
  parseJwt,
} from "../../utils";
import { setItem } from "../../../../utils/local-storage";
import { takeUIClickEvent } from "../../../../common/ga/ga";
import { TakeUIClickEventParameters } from "../../../../common/ga/types/ga-general-type";
import { isStandalone } from "../../../../utils/standalone";
import { ApplicationEnum } from "../../../../constants/constants";
import { useA2HS } from "../../../../utils/hooks/use-a2hs";
import { useQuery } from "../../../../utils/hooks/use-query";
import { SESSION_STORAGE_KEY } from "../../../../utils/session-storage";

interface HookProps {}

export const clearSessionStorage = () => {
  sessionStorage.removeItem(SESSION_STORAGE_KEY.LAST_PATH);
  sessionStorage.removeItem(SESSION_STORAGE_KEY.LAST_NAVIGATE_STATE);
  sessionStorage.removeItem(SESSION_STORAGE_KEY.LAST_AUTH_TOKEN);
};

export const useLogin = () => {
  initSurvey();
  const { t, i18n } = useTranslation();
  const userType = "CANDIDATE";

  // redux
  const dispatch = useDispatch();

  const token = useSelector(selectToken);
  const error = useSelector(selectError);
  const isLoading = useSelector(selectIsLoading);

  const navigate = useNavigate();
  const [region, setRegion] = useState<Region>({
    code: "+852",
    displayName: "Hong Kong",
  } as Region);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [islanguagePopupOpen, setIslanguagePopupOpen] = useState(false);
  const [isCandidateWebLoginEnabled, setIsCandidateWebLoginEnabled] = useState(true);
  const [enableStandardSSO, setEnableStandardSSO] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const { triggerA2HSPrompt, isInPWAMode } = useA2HS();
  const [userId, setUserId] = useState(""); // PHKL Custom -s
  const [enableCreateButton, setEnableCreateButton] = useState(false); // PHKL Custom -s

  // Fetch SAML policy

  const handleLogin = async () => {
    // change get user name logic.
    // PHKL Custom -s
    const username = getUserName();
    if (username.length === 0 || password.length === 0) {
      PruToast({
        message: t("Common.login_id_or_password_cannot_empty"),
        status: "error",
        duration: 3000,
      });
      return;
    }
    // PHKL Custom -e

    await dispatch<any>(loginAsync({ username, password, userType }));
    const token = getToken();
    const tokenData = token ? parseJwt(token) : {};

    // Check if there are stored routes and state."
    const lastPath = sessionStorage.getItem(SESSION_STORAGE_KEY.LAST_PATH);
    const navigateState = JSON.parse(
      sessionStorage.getItem(SESSION_STORAGE_KEY.LAST_NAVIGATE_STATE) || "{}",
    );
    const lastAuthToken = sessionStorage.getItem(SESSION_STORAGE_KEY.LAST_AUTH_TOKEN);

    if (tokenData && lastAuthToken) {
      const currentCandidateId = tokenData?.sub;
      const lasttCandidateId = parseJwt(lastAuthToken)?.sub;

      if (lastPath && currentCandidateId === lasttCandidateId) {
        navigate(lastPath, { state: navigateState });
        clearSessionStorage();
        return;
      }
    }
    if (tokenData?.role === RoleEnum.CANDIDATE) {
      // PHKL Custom -s
      const targetCandidate = getTargetCandidate();
      if (targetCandidate && !targetCandidate?.isNamesSet) {
        navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/edit-info`, {
          state: { candidateProfile: targetCandidate, isNew: true },
        });
      } else {
        // navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/candidate-home`);
        window.location.href = `${ROOT_PATH}/${ONBOARDING_PATH}/candidate-home`;
        backToApp();
      }
      // PHKL Custom -e
    } else if (tokenData?.role === RoleEnum.PENDING_CANDIDATE) {
      navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/join-now`, { state: { userName: username } });
    }
    clearSessionStorage();

    const gaData: TakeUIClickEventParameters = {
      module: "Login",
      feature: "CandidateLogin",
      journey: "login",
      stage: "Login",
      screen_id: "SCR_LGN",
      screen_name: "Login-Screen",
      object_name: "Login",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);
  };

  const handleCreateAccount = () => {
    navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/otp-verification`);

    const gaData: TakeUIClickEventParameters = {
      module: "Login",
      feature: "CandidateLogin",
      journey: "create_account",
      stage: "feature_entry",
      screen_id: "SCR_LGN",
      screen_name: "Login-Screen",
      object_name: "Create-Account",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);
  };

  const handleForgetPassword = () => {
    //PHKL Custom -s
    navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/forget-password`, {
      // state: { isForgetPassword: true },
    });
    //PHKL Custom -e
    const gaData: TakeUIClickEventParameters = {
      module: "Login",
      feature: "CandidateLogin",
      journey: "login",
      stage: "select_candidate_login",
      screen_id: "SCR_LGN",
      screen_name: "Login-Screen",
      object_name: "Candidate",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);
  };

  const handleDisclaimerClick = () => {
    navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/protocol`, {
      state: { title: t("Common.disclaimer"), des: t("Settings.disclaimer_content") },
    });
  };
  const handlePrivacyPolicyClick = () => {
    navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/protocol`, {
      state: { title: t("Common.privacy_notice"), des: t("Settings.privacy_policy_content") },
    });
  };

  const toggleLanguagePopupOpen = () => {
    setIslanguagePopupOpen(!islanguagePopupOpen);
  };

  const handleLanguageChange = (lng: any) => {
    setIslanguagePopupOpen(false);
    setItem("i18nextLng", lng);
    i18n.changeLanguage(lng);
  };
  const candidateWebPolicy = useRef<any>(null);
  // PHKL Custom -s
  const getUserName = (): string => {
    return userId ? userId : region.code && phoneNumber ? `${region.code} ${phoneNumber}` : "";
  };
  // PHKL Custom -e
  const query = useQuery();
  useEffect(() => {
    // PHKL Custom -s
    const isRedirectSaamLogout = sessionStorage.getItem("IS_REDIRECT_SAAM_LOGOUT");
    if (!isRedirectSaamLogout) {
      sessionStorage.setItem("IS_REDIRECT_SAAM_LOGOUT", "1");
      window.location.href = `${COMMON_PUBLIC_FRONTEND_ENDPOINT}/saam/logout`;
    }
    // PHKL Custom -e

    const ssoRedirect = query.get("ssoRedirect") || "";
    const fetchConfigs = async () => {
      try {
        const [globalConfig, recruitmentConfig] = await Promise.all([
          fetchGlobalConfig(),
          fetchRecruitmentPublicConfig(),
        ]);

        // Check candidate web login enablement
        const enableCandidateLogin = get(
          globalConfig,
          "data.content.onboardingConfig.enableCandidateLogin",
          false,
        );
        const candidateLoginChannel = get(
          recruitmentConfig,
          "data.content.Recruitment.candidateLoginChannel",
          [],
        );
        const flag = enableCandidateLogin && candidateLoginChannel.includes("WEB");
        setIsCandidateWebLoginEnabled(flag);
        const enableStandardSSO = get(
          recruitmentConfig,
          "data.content.Recruitment.enableStandardSSO",
          false,
        );
        setEnableStandardSSO(enableStandardSSO);
        if (ssoRedirect === "true" && enableStandardSSO) {
          handleSSOLogin();
        } else {
          setConfigLoaded(true);
        }
      } catch (error) {
        console.error("Error fetching configs:", error);
        setConfigLoaded(true); // Allow rendering even if fetch fails
      }
    };

    fetchConfigs();
  }, [dispatch]);

  // Handle SSO login
  const handleSSOLogin = async () => {
    const redirectUrl = "sso-callback";
    const application = ApplicationEnum.CANDIDATE_WEB;
    window.location.href = `${CORE_URL}/auth/aad?redirectUrl=${redirectUrl}&subscription-key=${get(
      window,
      "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY",
      "",
    )}&application=${application}&grantType=saml&idp=saam`;
  };

  useEffect(() => {
    dispatch<any>(getGlobalConfigAsync());
    if (!isStandalone()) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY.AUTH_TOKEN);
      dispatch(reset());
      dispatch(onboardingReset());
    }
  }, [dispatch]);

  useEffect(() => {
    let loginUser = token;
    if (getToken() !== fakeToken) {
      if (!loginUser && isStandalone()) {
        loginUser = getLoginUserData();
      }
      if (loginUser) {
        // PHKL Custom -s
        // if (!isInPWAMode) {
        //   triggerA2HSPrompt();
        // }
        // PHKL Custom -e

        if (loginUser?.role === RoleEnum.CANDIDATE) {
          // PHKL Custom -s
          const targetCandidate = getTargetCandidate();
          if (targetCandidate && !targetCandidate?.isNamesSet) {
            navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/edit-info`, {
              state: { candidateProfile: targetCandidate, isNew: true },
            });
          } else {
            // navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/candidate-home`);
            window.location.href = `${ROOT_PATH}/${ONBOARDING_PATH}/candidate-home`;
            backToApp();
          }
          // PHKL Custom -e
        } else if (loginUser?.role === RoleEnum.PENDING_CANDIDATE) {
          // PHKL Custom s
          const userName = getUserName() ? getUserName : sessionStorage.getItem("USER_NAME") ?? "";
          // PHKL Custom e

          if (userName) {
            navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/join-now`, {
              state: { userName },
            });
          }
        }
      }
    }

    if (error) {
      // PHKL Custom: -s
      const errMsgChangeLine = error?.includes("\n");
      PruToast({
        children: errMsgChangeLine && (
          <div style={{ justifyItems: "baseline" }}>
            {error?.split("\n")?.map((item) => (
              <div
                style={{
                  textAlign: "left",
                  wordBreak: "normal",
                  overflowWrap: "break-word",
                  textJustify: "inter-word",
                }}
              >
                {item}
              </div>
            ))}
          </div>
        ),
        message: !errMsgChangeLine ? error : undefined,
        // status: "error",
        duration: 3000,
      });
      // PHKL Custom: -e
      dispatch(reset());
    }
  }, [token, error, navigate, dispatch]);

  return {
    t,
    isCandidateWebLoginEnabled,
    region,
    setRegion,
    phoneNumber,
    handleLogin,
    setPhoneNumber,
    password,
    setPassword,
    isLoading,
    islanguagePopupOpen,
    toggleLanguagePopupOpen,
    handleLanguageChange,
    handleCreateAccount,
    handleForgetPassword,
    handleDisclaimerClick,
    handlePrivacyPolicyClick,
    handleSSOLogin,
    enableStandardSSO,
    configLoaded,
    // PHKL Custom -s
    userId,
    setUserId,
    enableCreateButton,
    setEnableCreateButton,
    // PHKL Custom -e
    query,
  };
};
